<template>
  <div class="nps-question__container">
    <HintText class="nps-question__hint-text">
      {{ c('select_answer') }}
    </HintText>
    <div class="nps-question__fields">
      <div
        v-for="rating of range(0, 11)"
        :key="`nps-field-${rating}`"
        :data-test-id="`NpsQuestion_Field-${rating}`"
        :class="{
          'nps-question__field': true,
          'nps-question__field--selected': npsAnswer.value.rating === rating,
          'nps-question__field--rtl': isRtl,
        }"
        role="option"
        :aria-selected="npsAnswer.value.rating === rating"
        @click="setNpsValue(rating)"
      >
        {{ rating }}
      </div>
    </div>
    <div class="nps-question__labels">
      <p>{{ c('nps.not_likely') }}</p>
      <p>{{ c('nps.extremely_likely') }}</p>
    </div>
  </div>
</template>
<script lang="ts">
import { range } from 'ramda'
import { computed, type DeepReadonly, defineComponent, type PropType } from 'vue'

import { type Answer } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import { isRtlLocale } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'

export default defineComponent({
  components: { HintText },
  props: {
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
  },
  emits: {
    setAnswer: (_: { fieldId: string; value: Answer<'nps'>['value'] }) => true,
  },
  setup(props, ctx) {
    const npsAnswer = computed(() => {
      const [npsAnswer] = props.answers.flatMap((answer) => {
        return answer.type === 'nps' ? [answer] : []
      })
      if (!npsAnswer) throw Error('Could not find nps answer')
      return npsAnswer
    })
    const setNpsValue = (rating: number): void => {
      const newRating = npsAnswer.value.value.rating === rating ? undefined : rating

      ctx.emit('setAnswer', { fieldId: 'nps', value: { rating: newRating } })
    }
    return { npsAnswer, setNpsValue, range, c, isRtl: isRtlLocale() }
  },
})
</script>
<style lang="postcss">
.nps-question {
  &__container {
    position: relative;
    margin: auto;
    width: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__fields {
    display: flex;
  }

  &__field {
    padding: 1rem;
    background-color: white;
    border: 1px solid var(--color-blue-grey);
    cursor: pointer;
    transition:
      background 0.2s,
      color 0.2s;

    &--selected {
      background-color: var(--color-selected);
      color: white;
    }

    &:not(:last-child) {
      border-right: 0;
    }

    &--rtl {
      &:not(:last-child) {
        border-right: 1px solid var(--color-blue-grey);
        border-left: 0;
      }
    }

    @media (max-width: 460px) {
      padding: 3vw;
    }

    @media (max-width: 320px) {
      padding: 2vw;
    }
  }

  &__labels {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    width: 100%;
  }

  &__hint-text {
    position: absolute;
    top: var(--attest-spacing-6);
    margin: 0 auto;
  }
}
</style>
